/* eslint-disable react/no-unescaped-entities, react-hooks/set-state-in-effect */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import api from '../../lib/api';
import * as LucideIcons from 'lucide-react';
import {
    CheckCircle2, Clock, Search, Filter, Activity,
    BookOpen, Info, Lock, Star, Sparkles, X, ArrowRight,
    Target, Users, Calendar, Layers, Zap, Code2, Lightbulb
} from 'lucide-react';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from '../../components/ui/sheet';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import Sparkline from '../../components/kpi/Sparkline';
import KPIObjectivePacks from '../../components/kpi/KPIObjectivePacks';

const STATUS_META = {
    live: { label: 'Actif', color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/20', icon: CheckCircle2 },
    partial: { label: 'Partiel', color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20', icon: Clock },
    roadmap: { label: 'Roadmap', color: 'text-white/40', bg: 'bg-white/5', border: 'border-white/10', icon: Lock },
};

const TIER_META = {
    essential: { label: 'Essentiel', color: 'text-slate-300', bg: 'bg-slate-500/10' },
    performance: { label: 'Performance', color: 'text-[#FF914D]', bg: 'bg-[#FF914D]/10' },
    intelligence: { label: 'Intelligence', color: 'text-[#9b4dca]', bg: 'bg-[#681793]/10' },
};

const UNIT_FORMATTERS = {
    pct: (v) => v === null || v === undefined ? '—' : `${v}%`,
    currency: (v) => v === null || v === undefined ? '—' : new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v),
    days: (v) => v === null || v === undefined ? '—' : `${v}j`,
    hours: (v) => v === null || v === undefined ? '—' : `${v}h`,
    count: (v) => v === null || v === undefined ? '—' : String(v),
    ratio: (v) => v === null || v === undefined ? '—' : String(v),
    text: (v) => v === null || v === undefined ? '—' : String(v),
};

// ─── Convertit la formule technique en phrase française lisible ───────
function humanizeFormula(formula) {
    if (!formula) return '';
    let s = String(formula);

    // 1) Comparisons with time windows (run BEFORE plain time windows)
    s = s.replace(/<\s*now\s*-\s*(\d+)\s*d\b/gi, "depuis plus de $1 jours");
    s = s.replace(/<\s*now\s*-\s*(\d+)\s*h\b/gi, "depuis plus de $1 heures");
    s = s.replace(/<\s*now\s*-\s*(\d+)\s*min\b/gi, "depuis plus de $1 minutes");
    s = s.replace(/>=\s*now\s*-\s*(\d+)\s*d\b/gi, "sur les $1 derniers jours");
    s = s.replace(/>=\s*now\s*-\s*(\d+)\s*h\b/gi, "sur les $1 dernières heures");

    // 2) Plain "less than X minutes" comparisons
    s = s.replace(/<\s*5\s*min\b/gi, 'en moins de 5 minutes');
    s = s.replace(/<\s*(\d+)\s*min\b/gi, 'en moins de $1 minutes');

    // 3) Remaining bare time windows
    s = s.replace(/now\s*-\s*(\d+)\s*d\b/gi, 'sur les $1 derniers jours');
    s = s.replace(/now\s*-\s*(\d+)\s*h\b/gi, 'depuis $1 heures');
    s = s.replace(/now\s*-\s*(\d+)\s*min\b/gi, 'depuis $1 minutes');

    // 4) Operators  
    s = s.replace(/!=/g, '≠');
    s = s.replace(/>=\s*/g, '≥ ');
    s = s.replace(/<=\s*/g, '≤ ');

    // 5) SQL keywords
    s = s.replace(/\bWHERE\b/gi, 'où');
    s = s.replace(/\bAND\b/gi, 'et');
    s = s.replace(/\bOR\b/gi, 'ou');
    s = s.replace(/\bIN\s*\(([^)]+)\)/gi, 'parmi ($1)');
    s = s.replace(/\bstatus\b/gi, 'statut');

    // 6) Aggregates
    s = s.replace(/COUNT\s*\(\s*([^)]*?)\s*\)/gi, 'Nombre de $1');
    s = s.replace(/AVG\s*\(\s*([^)]*?)\s*\)/gi, 'Moyenne de $1');
    s = s.replace(/SUM\s*\(\s*([^)]*?)\s*\)/gi, 'Somme de $1');
    s = s.replace(/MAX\s*\(\s*([^)]*?)\s*\)/gi, 'Maximum de $1');
    s = s.replace(/MIN\s*\(\s*([^)]*?)\s*\)/gi, 'Minimum de $1');

    // 7) Operators (after aggregates so we don't break their parentheses)
    s = s.replace(/\s*\/\s*/g, ' ÷ ');
    s = s.replace(/\s*\*\s*100/g, ' (× 100)');

    // 8) Column-like names
    s = s.replace(/created_at/g, 'date de création');
    s = s.replace(/qualified_at/g, 'date de qualification');
    s = s.replace(/first_contact_at/g, 'date du 1er contact');
    s = s.replace(/last_action/g, 'dernière action');
    s = s.replace(/updated_at/g, 'dernière mise à jour');
    s = s.replace(/signed_at/g, 'date de signature');
    s = s.replace(/closed_at/g, 'date de clôture');
    s = s.replace(/lead\./g, 'lead ');
    s = s.replace(/estimation\./g, 'estimation ');
    s = s.replace(/mandate\./g, 'mandat ');

    // 9) French contractions & cleanup
    s = s.replace(/Nombre de e/g, "Nombre d'e");
    s = s.replace(/Nombre de a/g, "Nombre d'a");
    s = s.replace(/Nombre de o/g, "Nombre d'o");
    s = s.replace(/Nombre de i/g, "Nombre d'i");
    s = s.replace(/Nombre de u/g, "Nombre d'u");
    s = s.replace(/Moyenne de e/g, "Moyenne d'e");
    s = s.replace(/Moyenne de a/g, "Moyenne d'a");
    s = s.replace(/\s{2,}/g, ' ').trim();

    // Capitalize first letter + ensure final period
    if (s.length > 0) s = s[0].toUpperCase() + s.slice(1);
    if (s && !/[.!?]$/.test(s)) s += '.';
    return s;
}

// ─── Helpful tips per family (contextual "Comment l'améliorer ?") ──
const IMPROVEMENT_TIPS = {
    lead_vendeur: [
        "Réagissez sous 5 min après réception d'un nouveau lead — le taux de conversion chute de 80% après 1h.",
        "Qualifiez chaque lead avec une fiche projet vendeur (motivation, urgence, prix souhaité).",
        "Filtrez les leads dormants > 72h en priorité absolue de la journée.",
    ],
    acquisition_source: [
        "Mesurez le coût d'acquisition par canal pour réallouer le budget vers les sources profitables.",
        "Intégrez l'origine du lead à la création (UTM, formulaire site, recommandation).",
    ],
    contact_rdv: [
        "Adoptez la règle des 5 min : tout lead reçu doit recevoir un contact sous 5 min ouvrées.",
        "Variez les canaux de contact : appel + SMS + email pour maximiser la joignabilité.",
        "Bloquez 30 min en début de journée dédiées aux relances de la veille.",
    ],
    estimation: [
        "Remettez l'estimation sous 48h après la visite : la conversion en mandat est x2 plus haute.",
        "Relancez systématiquement à J+3, J+7, J+14 si pas de retour vendeur.",
        "Ancrez le prix sur la donnée DVF officielle pour limiter les contre-offres.",
    ],
    mandate_exclusivity: [
        "Visez >60% d'exclusivité — chaque exclusif vaut 3x plus en CA qu'un simple.",
        "Si un mandat dort >30j sans visite, proposez un avenant prix ou un repositionnement.",
        "Maintenez un portefeuille équilibré : 70% commercialisation active + 30% pré-vente.",
    ],
    suivi_activity: [
        "Engagez-vous sur un point hebdo vendeur — la régularité est le 1er facteur de fidélisation.",
        "Loggez chaque appel/email/visite : ce qui n'est pas tracké n'est pas pilotable.",
    ],
    visits_offers: [
        "Faites systématiquement le compte-rendu de visite sous 2h via le QR code Homly.",
        "Si <2 visites/semaine sur un mandat, alertez le vendeur et envisagez un rebrief prix.",
        "Négociez chaque offre avec un argumentaire DVF + temps marché — pas à l'instinct.",
    ],
    compromis_acte_roi: [
        "Exigez un accord de principe bancaire AVANT signature compromis — divise par 2 les chutes.",
        "Suivi financement hebdo entre compromis et acte (J+30, J+60, J+75).",
    ],
    transfo_perf: [
        "Comparez vos taux de transformation à la moyenne agence — un écart de 10pts est exploitable.",
        "Identifiez les 2 KPIs de transformation où vous êtes le plus faible et focus 30 jours dessus.",
    ],
    predictive_ai: [
        "Faites confiance aux signaux IA en début de journée pour prioriser vos actions.",
        "Plus vous loggez de données (appels, visites, offres), plus l'IA devient précise.",
    ],
};

const AUDIENCE_LABELS = { admin: 'Direction', manager: 'Manager', agent: 'Agent', superadmin: 'Super Admin' };
const LEVEL_LABELS = { agency: 'Agence', agent: 'Agent', mandate: 'Mandat', lead: 'Lead' };
const FREQUENCY_LABELS = { daily: 'Quotidien', weekly: 'Hebdo', monthly: 'Mensuel', quarterly: 'Trimestriel' };
const PRIORITY_LABELS = { high: 'Haute', medium: 'Moyenne', low: 'Faible' };

// ─── KPI Detail Sheet ─────────────────────────────────────────────────
function KPIDetailSheet({ kpi, result, isFavorite, onToggleFavorite, families, open, onOpenChange }) {
    const [history, setHistory] = useState(null);
    const [loadingHistory, setLoadingHistory] = useState(false);

    useEffect(() => {
        if (!open || !kpi || kpi.status === 'roadmap') {
            setHistory(null);
            return;
        }
        let cancelled = false;
        setLoadingHistory(true);
        (async () => {
            try {
                const res = await api.get(`/kpi/history/${kpi.id}`, { params: { days: 30 } });
                if (!cancelled) setHistory(res.data);
            } catch {
                if (!cancelled) setHistory(null);
            } finally {
                if (!cancelled) setLoadingHistory(false);
            }
        })();
        return () => { cancelled = true; };
    }, [open, kpi]);

    if (!kpi) return null;
    const status = STATUS_META[kpi.status];
    const tier = TIER_META[kpi.tier];
    const StatusIcon = status.icon;
    const formatter = UNIT_FORMATTERS[kpi.unit] || UNIT_FORMATTERS.text;
    const value = result?.value;
    const family = families?.find(f => f.id === kpi.family);
    const tips = IMPROVEMENT_TIPS[kpi.family] || [];

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="right"
                className="w-full sm:max-w-xl overflow-y-auto bg-background border-l border-border p-0"
                data-testid="kpi-detail-sheet"
            >
                {/* Header — léger, peu de bordures */}
                <div className="px-6 pt-6 pb-4">
                    <div className="flex items-start gap-3 pr-8">
                        <div className={`w-9 h-9 rounded-xl ${status.bg} ${status.color} flex items-center justify-center flex-shrink-0`}>
                            <StatusIcon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 mb-1 text-[10px] font-semibold uppercase tracking-wider">
                                <span className={status.color}>{status.label}</span>
                                <span className="text-muted-foreground/40">·</span>
                                <span className="text-muted-foreground">{family?.label || kpi.family}</span>
                            </div>
                            <SheetTitle className="text-xl font-black text-foreground leading-tight">
                                {kpi.name}
                            </SheetTitle>
                        </div>
                        <button
                            onClick={() => onToggleFavorite(kpi.id)}
                            className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                                isFavorite
                                    ? 'bg-[#FF914D]/20 text-[#FF914D] hover:bg-[#FF914D]/30'
                                    : 'bg-white/[0.04] text-muted-foreground hover:bg-white/10 hover:text-white'
                            }`}
                            data-testid={`sheet-star-${kpi.id}`}
                            title={isFavorite ? 'Retirer des suivis' : 'Suivre ce KPI'}
                        >
                            <Star className={`w-4 h-4 ${isFavorite ? 'fill-current' : ''}`} />
                        </button>
                    </div>
                </div>

                <div className="px-6 pb-8 space-y-6">
                    {/* Current value + sparkline (combined card) */}
                    {kpi.status !== 'roadmap' && (
                        <div className="rounded-2xl bg-white/[0.02] border border-white/5 p-5">
                            <div className="flex items-end justify-between gap-4 mb-3">
                                <div>
                                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground/70 font-semibold mb-1">
                                        Valeur actuelle
                                    </p>
                                    <p className={`text-4xl font-black tabular-nums leading-none ${
                                        kpi.status === 'partial' ? 'text-amber-400' : 'text-foreground'
                                    }`}>
                                        {formatter(value)}
                                    </p>
                                    {kpi.status === 'partial' && (
                                        <p className="text-[11px] text-amber-400/80 mt-1.5">
                                            Données partielles
                                        </p>
                                    )}
                                </div>
                                {history && history.points && history.points.length > 0 && (
                                    <div className="flex-1 max-w-[180px] sm:max-w-[240px] relative">
                                        <Sparkline
                                            points={history.points}
                                            synthetic={history.synthetic}
                                            color={kpi.status === 'partial' ? '#fbbf24' : '#FF914D'}
                                            height={56}
                                        />
                                    </div>
                                )}
                                {loadingHistory && !history && (
                                    <div className="flex-1 max-w-[240px] h-14 rounded-lg bg-white/[0.02] animate-pulse" />
                                )}
                            </div>
                        </div>
                    )}

                    {/* Definition — À quoi ça sert ? (clean, no card chrome) */}
                    <section>
                        <h3 className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground mb-2">
                            <Info className="w-3 h-3 text-[#FF914D]" />
                            À quoi ça sert
                        </h3>
                        <p className="text-sm text-foreground/90 leading-relaxed">
                            {kpi.definition}
                        </p>
                    </section>

                    {/* Formula — Comment c'est calculé (en clair) */}
                    {kpi.formula && (
                        <section>
                            <h3 className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground mb-2">
                                <Code2 className="w-3 h-3 text-[#FF914D]" />
                                Comment c'est calculé
                            </h3>
                            <p className="text-sm text-foreground/80 leading-relaxed italic">
                                {humanizeFormula(kpi.formula)}
                            </p>
                        </section>
                    )}

                    {/* Meta grid — minimal */}
                    <section>
                        <h3 className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground mb-2">
                            <Layers className="w-3 h-3 text-[#FF914D]" />
                            Métadonnées
                        </h3>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                            <MetaItem icon={Users} label="Audience"
                                value={(kpi.audience || []).map(a => AUDIENCE_LABELS[a] || a).join(' · ') || '—'} />
                            <MetaItem icon={Target} label="Niveau" value={LEVEL_LABELS[kpi.level] || kpi.level} />
                            <MetaItem icon={Calendar} label="Fréquence" value={FREQUENCY_LABELS[kpi.frequency] || kpi.frequency} />
                            <MetaItem icon={Zap} label="Priorité" value={PRIORITY_LABELS[kpi.priority] || kpi.priority} />
                        </div>
                        <div className="mt-3">
                            <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold ${tier.color} ${tier.bg}`}>
                                Niveau {tier.label}
                            </span>
                        </div>
                    </section>

                    {/* Required (roadmap KPIs) */}
                    {kpi.status === 'roadmap' && kpi.requires?.length > 0 && (
                        <section className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4">
                            <h3 className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.15em] text-amber-400 mb-2">
                                <Lock className="w-3 h-3" />
                                Pour activer ce KPI
                            </h3>
                            <p className="text-xs text-muted-foreground mb-2">Briques nécessaires :</p>
                            <div className="flex flex-wrap gap-1.5">
                                {kpi.requires.map(r => (
                                    <span key={r} className="px-2 py-1 rounded text-[10px] bg-amber-500/10 text-amber-300 border border-amber-500/20 font-mono">
                                        {r}
                                    </span>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Improvement tips — clean cards */}
                    {kpi.status !== 'roadmap' && tips.length > 0 && (
                        <section>
                            <h3 className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground mb-2">
                                <Lightbulb className="w-3 h-3 text-[#FF914D]" />
                                Comment l'améliorer
                            </h3>
                            <ul className="space-y-2">
                                {tips.map((tip, i) => (
                                    <li key={i} className="flex items-start gap-3 text-sm text-foreground/80 py-2 px-3 rounded-lg hover:bg-white/[0.02] transition-colors">
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#FF914D]/15 text-[#FF914D] text-[10px] font-bold flex items-center justify-center mt-0.5">
                                            {i + 1}
                                        </span>
                                        <span className="leading-relaxed">{tip}</span>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    {/* Footer CTA */}
                    <div className="flex items-center gap-2 pt-2">
                        <button
                            onClick={() => onToggleFavorite(kpi.id)}
                            className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-all ${
                                isFavorite
                                    ? 'bg-[#FF914D]/15 text-[#FF914D] border border-[#FF914D]/30 hover:bg-[#FF914D]/25'
                                    : 'bg-gradient-to-r from-[#681793] to-[#FF914D] text-white shadow-lg shadow-[#FF914D]/20 hover:opacity-90'
                            }`}
                            data-testid={`sheet-cta-favorite-${kpi.id}`}
                        >
                            <Star className={`w-4 h-4 ${isFavorite ? 'fill-current' : ''}`} />
                            {isFavorite ? 'Suivi · cliquer pour retirer' : 'Suivre ce KPI'}
                        </button>
                    </div>

                    <p className="text-[10px] text-muted-foreground/50 text-center pt-1">
                        ID technique : <span className="font-mono">{kpi.id}</span>
                    </p>
                </div>
            </SheetContent>
        </Sheet>
    );
}

function MetaItem({ icon: Icon, label, value }) {
    return (
        <div className="flex items-start gap-2 py-1">
            <Icon className="w-3 h-3 text-muted-foreground/50 flex-shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
                <p className="text-[9px] uppercase tracking-wider text-muted-foreground/60 font-semibold">{label}</p>
                <p className="text-xs text-foreground font-medium capitalize">{value}</p>
            </div>
        </div>
    );
}

// ─── Star toggle button ──────────────────────────────────────────────
function StarButton({ active, onToggle, kpiId }) {
    return (
        <button
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
            className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-all ${
                active
                    ? 'bg-[#FF914D]/20 text-[#FF914D] hover:bg-[#FF914D]/30'
                    : 'bg-white/5 text-muted-foreground hover:bg-white/10 hover:text-white'
            }`}
            title={active ? 'Retirer des suivis' : 'Suivre ce KPI'}
            data-testid={`kpi-star-${kpiId}`}
        >
            <Star className={`w-3.5 h-3.5 ${active ? 'fill-current' : ''}`} />
        </button>
    );
}

// ─── Compact tracked-KPI tile (top section) ──────────────────────────
function TrackedKpiTile({ entry, onUntrack, onOpenDetail, history }) {
    const { kpi, result } = entry;
    const formatter = UNIT_FORMATTERS[kpi.unit] || UNIT_FORMATTERS.text;
    const value = result?.value;
    const statusDot = kpi.status === 'live' ? 'bg-green-400' :
                      kpi.status === 'partial' ? 'bg-amber-400' : 'bg-muted-foreground/40';

    return (
        <motion.button
            layout
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.18 }}
            onClick={() => onOpenDetail(kpi.id)}
            className="group relative text-left p-4 rounded-xl border border-white/[0.06] bg-white/[0.015] hover:bg-white/[0.04] hover:border-[#FF914D]/25 transition-all"
            data-testid={`tracked-tile-${kpi.id}`}
        >
            <span
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); onUntrack(kpi.id); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onUntrack(kpi.id); } }}
                className="absolute top-2 right-2 w-6 h-6 rounded-full bg-black/30 text-muted-foreground opacity-0 group-hover:opacity-100 hover:bg-rose-500/20 hover:text-rose-300 flex items-center justify-center transition-all cursor-pointer"
                title="Ne plus suivre"
                data-testid={`untrack-${kpi.id}`}
            >
                <X className="w-3 h-3" />
            </span>
            <div className="flex items-center gap-1.5 mb-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${statusDot}`} />
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground/70 font-semibold">{kpi.family.replace('_', ' ')}</span>
            </div>
            <h3 className="text-xs font-semibold text-foreground/90 leading-tight mb-2 pr-6 line-clamp-2 min-h-[2rem]">
                {kpi.name}
            </h3>
            <div className="flex items-end justify-between gap-2">
                <p className="text-2xl font-black text-foreground leading-none tabular-nums">
                    {formatter(value)}
                </p>
                {history?.points?.length > 0 && (
                    <div className="flex-1 max-w-[80px] opacity-80">
                        <Sparkline
                            points={history.points}
                            synthetic={history.synthetic}
                            height={24}
                            width={120}
                            showLast={false}
                            color="#FF914D"
                            fillOpacity={0.1}
                        />
                    </div>
                )}
            </div>
        </motion.button>
    );
}

// ─── Full KPI card (catalog) ─────────────────────────────────────────
function KPICard({ kpi, result, isFavorite, onToggleFavorite, onOpenDetail, history }) {
    const tier = TIER_META[kpi.tier];
    const formatter = UNIT_FORMATTERS[kpi.unit] || UNIT_FORMATTERS.text;
    const value = result?.value;

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={() => onOpenDetail(kpi.id)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenDetail(kpi.id); } }}
            role="button"
            tabIndex={0}
            className={`text-left p-4 rounded-xl border bg-white/[0.015] hover:bg-white/[0.04] transition-all h-full flex flex-col group cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#FF914D]/40 ${
                isFavorite ? 'border-[#FF914D]/30 ring-1 ring-[#FF914D]/20' : 'border-white/[0.06] hover:border-white/15'
            }`}
            data-testid={`kpi-card-${kpi.id}`}
        >
            <div className="flex items-start justify-between gap-2 mb-2">
                <h3 className="text-sm font-bold text-foreground leading-tight flex-1">{kpi.name}</h3>
                <div className="flex items-center gap-1.5 shrink-0">
                    <span className={`w-1.5 h-1.5 rounded-full ${
                        kpi.status === 'live' ? 'bg-green-400' :
                        kpi.status === 'partial' ? 'bg-amber-400' : 'bg-muted-foreground/40'
                    }`} title={kpi.status} />
                    <StarButton
                        active={isFavorite}
                        onToggle={() => onToggleFavorite(kpi.id)}
                        kpiId={kpi.id}
                    />
                </div>
            </div>

            {kpi.status === 'live' ? (
                <div className="my-2 flex items-end justify-between gap-2">
                    <p className="text-2xl font-black text-foreground leading-none tabular-nums">{formatter(value)}</p>
                    {history?.points?.length > 0 && (
                        <div className="flex-1 max-w-[90px] opacity-80">
                            <Sparkline
                                points={history.points}
                                synthetic={history.synthetic}
                                height={24}
                                width={120}
                                showLast={false}
                                color="#FF914D"
                                fillOpacity={0.1}
                            />
                        </div>
                    )}
                </div>
            ) : kpi.status === 'partial' ? (
                <div className="my-2 flex items-end justify-between gap-2">
                    <div>
                        <p className="text-xl font-black text-amber-400 leading-none tabular-nums">{formatter(value)}</p>
                        <p className="text-[10px] text-amber-400/70 mt-0.5">Partiel</p>
                    </div>
                    {history?.points?.length > 0 && (
                        <div className="flex-1 max-w-[90px] opacity-70">
                            <Sparkline
                                points={history.points}
                                synthetic={history.synthetic}
                                height={24}
                                width={120}
                                showLast={false}
                                color="#fbbf24"
                                fillOpacity={0.1}
                            />
                        </div>
                    )}
                </div>
            ) : (
                <div className="my-2 flex items-center gap-1.5">
                    <Lock className="w-3 h-3 text-muted-foreground/50" />
                    <p className="text-[11px] text-muted-foreground/60">Sur la roadmap</p>
                </div>
            )}

            <p className="text-[11px] text-muted-foreground/80 leading-relaxed mt-1 line-clamp-2 flex-1">{kpi.definition}</p>

            <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-white/[0.04] text-[10px]">
                <span className={`${tier.color} font-semibold`}>{tier.label}</span>
                <span className="text-muted-foreground/30">·</span>
                <span className="text-muted-foreground/60">{(kpi.audience || []).map(a => AUDIENCE_LABELS[a] || a).join(' · ')}</span>
                <span className="ml-auto text-muted-foreground/40 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    Détails <ArrowRight className="w-2.5 h-2.5" />
                </span>
            </div>
        </motion.div>
    );
}

// ─── Family card ─────────────────────────────────────────────────────
function FamilyCard({ family, onClick, active }) {
    const Icon = LucideIcons[family.icon] || Activity;
    return (
        <motion.button
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.98 }}
            onClick={onClick}
            className={`text-left p-4 rounded-xl border transition-all ${
                active
                    ? 'bg-gradient-to-br from-[#681793]/15 to-[#FF914D]/10 border-[#FF914D]/30 shadow-lg shadow-[#FF914D]/5'
                    : 'bg-white/[0.015] border-white/[0.06] hover:bg-white/[0.04] hover:border-white/15'
            }`}
            data-testid={`family-tile-${family.id}`}
        >
            <div className="flex items-center justify-between mb-2.5">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                    active ? 'bg-gradient-to-br from-[#681793] to-[#FF914D] shadow-md shadow-[#FF914D]/20' : 'bg-white/[0.04]'
                }`}>
                    <Icon className={`w-4 h-4 ${active ? 'text-white' : 'text-muted-foreground'}`} />
                </div>
                <span className={`text-[10px] font-bold ${active ? 'text-[#FF914D]' : 'text-muted-foreground/60'}`}>{family.counts.total}</span>
            </div>
            <p className="text-sm font-bold text-foreground leading-tight mb-1">{family.label}</p>
            <div className="flex gap-1.5 text-[10px]">
                <span className="text-green-400/90 font-semibold">{family.counts.live} actifs</span>
                {family.counts.partial > 0 && <span className="text-amber-400/70">· {family.counts.partial}</span>}
            </div>
        </motion.button>
    );
}

// ═════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═════════════════════════════════════════════════════════════════════
export default function KPICatalogPage() {
    const [doc, setDoc] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeFamily, setActiveFamily] = useState(null);
    const [search, setSearch] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterTier, setFilterTier] = useState('all');
    const [showOnlyFavorites, setShowOnlyFavorites] = useState(false);
    const [computedResults, setComputedResults] = useState({});
    const [favoriteIds, setFavoriteIds] = useState([]);
    const [trackedKPIs, setTrackedKPIs] = useState([]);
    const [trackedLoading, setTrackedLoading] = useState(true);
    const [detailKpiId, setDetailKpiId] = useState(null);
    const [detailResult, setDetailResult] = useState(null);
    const [historyByKpi, setHistoryByKpi] = useState({}); // kpi_id -> { points, synthetic }

    // Look up the selected KPI from doc + tracked
    const detailKpi = useMemo(() => {
        if (!detailKpiId) return null;
        if (doc) {
            for (const fam of doc.families) {
                const found = fam.kpis.find(k => k.id === detailKpiId);
                if (found) return found;
            }
        }
        const tr = trackedKPIs.find(e => e.kpi.id === detailKpiId);
        return tr?.kpi || null;
    }, [detailKpiId, doc, trackedKPIs]);

    // Fetch live value for the detail KPI when opened (if not already in computedResults / tracked)
    useEffect(() => {
        if (!detailKpiId || !detailKpi) { setDetailResult(null); return; }
        // Try cache first
        const cached = computedResults[detailKpiId];
        const tracked = trackedKPIs.find(e => e.kpi.id === detailKpiId);
        if (cached) { setDetailResult(cached); return; }
        if (tracked?.result) { setDetailResult(tracked.result); return; }
        // Fetch fresh if live/partial
        if (detailKpi.status === 'roadmap') { setDetailResult(null); return; }
        (async () => {
            try {
                const res = await api.get('/kpi/compute', { params: { ids: detailKpiId } });
                setDetailResult(res.data?.results?.[detailKpiId] || null);
            } catch { setDetailResult(null); }
        })();
    }, [detailKpiId, detailKpi, computedResults, trackedKPIs]);

    const openDetail = (kpiId) => setDetailKpiId(kpiId);
    const closeDetail = (open) => { if (!open) setDetailKpiId(null); };

    // Load catalog
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await api.get('/kpi/documentation');
                if (!cancelled) {
                    setDoc(res.data);
                    if (res.data.families.length && !activeFamily) {
                        setActiveFamily(res.data.families[0].id);
                    }
                }
            } catch { /* silent */ }
            finally { if (!cancelled) setLoading(false); }
        })();
        return () => { cancelled = true; };
    }, []);

    // Load favorites (on mount + after toggles)
    const loadFavorites = useCallback(async () => {
        setTrackedLoading(true);
        try {
            const res = await api.get('/kpi/favorites');
            const items = res.data?.favorites || [];
            setTrackedKPIs(items);
            setFavoriteIds(items.map(e => e.kpi.id));
        } catch { /* silent */ }
        finally { setTrackedLoading(false); }
    }, []);

    useEffect(() => { loadFavorites(); }, [loadFavorites]);

    // Compute live KPIs of active family
    useEffect(() => {
        if (!activeFamily || !doc) return;
        const fam = doc.families.find(f => f.id === activeFamily);
        if (!fam) return;
        const liveIds = fam.kpis.filter(k => k.status === 'live' || k.status === 'partial').map(k => k.id);
        if (!liveIds.length) { setComputedResults({}); return; }
        (async () => {
            try {
                const res = await api.get('/kpi/compute', { params: { ids: liveIds.join(',') } });
                setComputedResults(res.data.results || {});
            } catch { /* silent */ }
        })();
    }, [activeFamily, doc]);

    // Batch-fetch sparkline histories for visible family + tracked KPIs (cached, no refetch)
    useEffect(() => {
        if (!doc) return;
        const ids = new Set();
        if (activeFamily) {
            const fam = doc.families.find(f => f.id === activeFamily);
            (fam?.kpis || [])
                .filter(k => k.status === 'live' || k.status === 'partial')
                .forEach(k => ids.add(k.id));
        }
        trackedKPIs.forEach(t => {
            if (t.kpi.status !== 'roadmap') ids.add(t.kpi.id);
        });
        const missing = [...ids].filter(id => !historyByKpi[id]);
        if (!missing.length) return;
        (async () => {
            try {
                const res = await api.get('/kpi/history-batch', { params: { ids: missing.join(','), days: 30 } });
                const map = res.data?.results || {};
                setHistoryByKpi(prev => ({ ...prev, ...map }));
            } catch { /* silent */ }
        })();
    }, [activeFamily, doc, trackedKPIs, historyByKpi]);

    // Toggle favorite
    const toggleFavorite = async (kpiId) => {
        const wasFav = favoriteIds.includes(kpiId);
        // Optimistic update
        setFavoriteIds(prev => wasFav ? prev.filter(id => id !== kpiId) : [...prev, kpiId]);
        try {
            await api.post(`/kpi/favorites/${kpiId}`);
            await loadFavorites();
            toast.success(wasFav ? 'KPI retiré des suivis' : 'KPI ajouté à vos suivis', {
                duration: 2000,
            });
        } catch (e) {
            // Rollback
            setFavoriteIds(prev => wasFav ? [...prev, kpiId] : prev.filter(id => id !== kpiId));
            toast.error('Erreur lors de la mise à jour');
        }
    };

    const currentFamily = useMemo(() => {
        if (!doc || !activeFamily) return null;
        return doc.families.find(f => f.id === activeFamily);
    }, [doc, activeFamily]);

    // Map id -> kpi (toutes familles) pour les Packs d'objectifs
    const allKpisById = useMemo(() => {
        const m = {};
        if (doc) doc.families.forEach(f => f.kpis.forEach(k => { m[k.id] = k; }));
        return m;
    }, [doc]);

    // Active un pack d'objectifs : suit tous ses KPIs non encore suivis
    const activatePack = async (pack, validIds) => {
        const toAdd = validIds.filter(id => !favoriteIds.includes(id));
        if (toAdd.length === 0) {
            toast.info('Tous les KPIs de ce pack sont déjà suivis');
            return;
        }
        setFavoriteIds(prev => [...new Set([...prev, ...toAdd])]);
        try {
            await Promise.all(toAdd.map(id => api.post(`/kpi/favorites/${id}`)));
            await loadFavorites();
            toast.success(`Pack "${pack.name}" activé · ${toAdd.length} KPI ajoutés à vos suivis`, { duration: 2500 });
        } catch {
            await loadFavorites();
            toast.error("Erreur lors de l'activation du pack");
        }
    };

    const filteredKPIs = useMemo(() => {
        if (!currentFamily) return [];
        let kpis = currentFamily.kpis;
        if (search) {
            const s = search.toLowerCase();
            kpis = kpis.filter(k => k.name.toLowerCase().includes(s) || k.definition.toLowerCase().includes(s));
        }
        if (filterStatus !== 'all') kpis = kpis.filter(k => k.status === filterStatus);
        if (filterTier !== 'all') kpis = kpis.filter(k => k.tier === filterTier);
        if (showOnlyFavorites) kpis = kpis.filter(k => favoriteIds.includes(k.id));
        return kpis;
    }, [currentFamily, search, filterStatus, filterTier, showOnlyFavorites, favoriteIds]);

    if (loading) return <div className="animate-pulse h-96 bg-card rounded-2xl border border-border" />;
    if (!doc) return <div className="p-8 text-center text-muted-foreground">Erreur de chargement du catalogue</div>;

    return (
        <div className="space-y-6" data-testid="kpi-catalog-page">
            {/* Header (more breathing) */}
            <div className="flex items-end justify-between flex-wrap gap-3 pb-2">
                <div>
                    <h1 className="text-2xl md:text-3xl font-black text-foreground tracking-tight flex items-center gap-2.5">
                        <BookOpen className="w-6 h-6 text-[#FF914D]" />
                        Catalogue KPI
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        <span className="text-foreground font-bold">{doc.summary.total_kpis}</span> KPI ·
                        <span className="text-green-400 font-semibold ml-1">{doc.summary.by_status.live} actifs</span>
                        {doc.summary.by_status.partial > 0 && (
                            <>·<span className="text-amber-400 ml-1">{doc.summary.by_status.partial} partiels</span></>
                        )}
                        {trackedKPIs.length > 0 && (
                            <>·<span className="text-[#FF914D] font-semibold ml-1">{trackedKPIs.length} suivi{trackedKPIs.length > 1 ? 's' : ''}</span></>
                        )}
                    </p>
                </div>
            </div>

            {/* ═══ Tracked KPIs section ════════════════════════════════════ */}
            <div className="rounded-2xl bg-gradient-to-br from-[#FF914D]/[0.04] via-transparent to-[#681793]/[0.04] border border-white/[0.06] p-5">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#FF914D] to-[#681793] flex items-center justify-center shadow-lg shadow-[#FF914D]/15">
                            <Star className="w-3.5 h-3.5 text-white fill-white" />
                        </div>
                        <div>
                            <h2 className="text-sm font-black text-foreground">Mes KPIs suivis</h2>
                            <p className="text-[11px] text-muted-foreground/80">
                                Les indicateurs que vous voulez voir en priorité
                            </p>
                        </div>
                    </div>
                    {trackedKPIs.length > 0 && (
                        <span className="px-2 py-1 rounded-full text-[10px] font-bold bg-[#FF914D]/10 text-[#FF914D]">
                            {trackedKPIs.length}
                        </span>
                    )}
                </div>

                {trackedLoading ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[1, 2, 3, 4].map(i => (
                            <div key={i} className="h-28 rounded-xl bg-white/[0.02] animate-pulse" />
                        ))}
                    </div>
                ) : trackedKPIs.length === 0 ? (
                    <div className="text-center py-8 px-4">
                        <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-white/[0.03] mb-3">
                            <Sparkles className="w-5 h-5 text-[#FF914D]" />
                        </div>
                        <p className="text-sm font-semibold text-foreground mb-1">
                            Aucun KPI suivi pour l'instant
                        </p>
                        <p className="text-xs text-muted-foreground max-w-md mx-auto leading-relaxed">
                            Cliquez sur l'étoile <Star className="w-3 h-3 inline -translate-y-0.5 text-[#FF914D]" /> à côté
                            d'un KPI dans le catalogue ci-dessous pour le suivre ici en temps réel.
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="tracked-grid">
                        <AnimatePresence mode="popLayout">
                            {trackedKPIs.map(entry => (
                                <TrackedKpiTile
                                    key={entry.kpi.id}
                                    entry={entry}
                                    onUntrack={toggleFavorite}
                                    onOpenDetail={openDetail}
                                    history={historyByKpi[entry.kpi.id]}
                                />
                            ))}
                        </AnimatePresence>
                    </div>
                )}
            </div>

            {/* ═══ Packs d'objectifs (recommandés Homly) ═══════════════════ */}
            <KPIObjectivePacks
                allKpisById={allKpisById}
                favoriteIds={favoriteIds}
                onActivate={activatePack}
            />

            {/* Families grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {doc.families.map(fam => (
                    <FamilyCard
                        key={fam.id}
                        family={fam}
                        active={activeFamily === fam.id}
                        onClick={() => setActiveFamily(fam.id)}
                    />
                ))}
            </div>

            {/* Current family */}
            {currentFamily && (
                <div className="space-y-4">
                    <div className="px-1 pb-1">
                        <h2 className="text-lg font-black text-foreground">{currentFamily.label}</h2>
                        <p className="text-xs text-muted-foreground/80 mt-0.5">{currentFamily.description}</p>
                    </div>

                    {/* Filters — minimal toolbar */}
                    <div className="flex flex-wrap items-center gap-2">
                        <div className="relative flex-1 min-w-[200px]">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground/60" />
                            <input
                                type="text"
                                placeholder="Rechercher un KPI…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="w-full pl-9 pr-3 py-2 rounded-lg bg-white/[0.02] border border-white/[0.06] text-foreground text-sm placeholder-white/30 focus:border-[#FF914D]/40 focus:bg-white/[0.04] focus:outline-none transition-colors"
                                data-testid="kpi-search"
                            />
                        </div>
                        <Select value={filterStatus} onValueChange={setFilterStatus}>
                            <SelectTrigger className="w-[160px] bg-white/[0.02] border-white/[0.06] text-sm text-foreground focus:border-[#FF914D]/40 focus:ring-0" data-testid="kpi-filter-status">
                                <SelectValue placeholder="Statut" />
                            </SelectTrigger>
                            <SelectContent className="bg-card border-border">
                                <SelectItem value="all">Tous statuts</SelectItem>
                                <SelectItem value="live">Actifs</SelectItem>
                                <SelectItem value="partial">Partiels</SelectItem>
                                <SelectItem value="roadmap">Roadmap</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={filterTier} onValueChange={setFilterTier}>
                            <SelectTrigger className="w-[160px] bg-white/[0.02] border-white/[0.06] text-sm text-foreground focus:border-[#FF914D]/40 focus:ring-0" data-testid="kpi-filter-tier">
                                <SelectValue placeholder="Niveau" />
                            </SelectTrigger>
                            <SelectContent className="bg-card border-border">
                                <SelectItem value="all">Tous niveaux</SelectItem>
                                <SelectItem value="essential">Essentiel</SelectItem>
                                <SelectItem value="performance">Performance</SelectItem>
                                <SelectItem value="intelligence">Intelligence</SelectItem>
                            </SelectContent>
                        </Select>
                        <button
                            onClick={() => setShowOnlyFavorites(v => !v)}
                            className={`px-3 py-2 rounded-lg border text-sm font-medium flex items-center gap-1.5 transition-all ${
                                showOnlyFavorites
                                    ? 'bg-[#FF914D]/15 border-[#FF914D]/40 text-[#FF914D]'
                                    : 'bg-white/[0.02] border-white/[0.06] text-muted-foreground hover:text-white hover:bg-white/[0.04]'
                            }`}
                            data-testid="kpi-filter-favorites"
                            title="Afficher uniquement les KPIs suivis"
                        >
                            <Star className={`w-3.5 h-3.5 ${showOnlyFavorites ? 'fill-current' : ''}`} />
                            Suivis
                        </button>
                    </div>

                    {/* KPI grid */}
                    {filteredKPIs.length === 0 ? (
                        <div className="p-12 text-center bg-card rounded-2xl border border-border">
                            <Filter className="w-8 h-8 text-muted-foreground mx-auto mb-2 opacity-50" />
                            <p className="text-sm text-muted-foreground">
                                {showOnlyFavorites
                                    ? "Aucun KPI suivi dans cette famille — cliquez sur l'étoile d'un KPI pour le suivre"
                                    : "Aucun KPI ne correspond à vos filtres"}
                            </p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {filteredKPIs.map(kpi => (
                                <KPICard
                                    key={kpi.id}
                                    kpi={kpi}
                                    result={computedResults[kpi.id]}
                                    isFavorite={favoriteIds.includes(kpi.id)}
                                    onToggleFavorite={toggleFavorite}
                                    onOpenDetail={openDetail}
                                    history={historyByKpi[kpi.id]}
                                />
                            ))}
                        </div>
                    )}

                    {/* Discrete legend — minimal */}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-1 pt-2 text-[10px] text-muted-foreground/60">
                        <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-green-400" />Actif</span>
                        <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-amber-400" />Partiel</span>
                        <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50" />Roadmap</span>
                        <span className="flex items-center gap-1.5"><Star className="w-2.5 h-2.5 text-[#FF914D] fill-[#FF914D]" />Cliquez sur l'étoile pour suivre</span>
                    </div>
                </div>
            )}

            {/* KPI Detail Sheet */}
            <KPIDetailSheet
                kpi={detailKpi}
                result={detailResult}
                isFavorite={detailKpi ? favoriteIds.includes(detailKpi.id) : false}
                onToggleFavorite={toggleFavorite}
                families={doc?.families}
                open={!!detailKpiId}
                onOpenChange={closeDetail}
            />
        </div>
    );
}
